import React, { useEffect, useMemo, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { TanTable } from '../../../components/TanTable';
import quoteService, { type QuoteHistoryRow } from '../../../services/quote.service';

interface Props {
  quoteNumber: number | undefined;
}

const TABLE_LABELS: Record<string, string> = {
  masterquote:            'Quote Header',
  quoterevisions:         'Revision',
  transloadquote:         'Dray/Warehouse',
  directquote:            'Direct',
  deliveryquote:          'Delivery Lane',
  productcoderates:       'Product Rate',
  quoteadditionalcharges: 'Tariff Charge',
  stopoffs:               'Stopoff',
  notes:                  'Note',
};

/** The endpoint returns datetime preformatted as "MM/DD/YYYY HH:MM", which does
 *  NOT sort chronologically as a plain string. Re-key it to "YYYYMMDDHHMM" so
 *  the Date/Time column header sorts in true time order. */
const _dtSortKey = (s: string): string => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{1,2}):(\d{2})/.exec(s ?? '');
  return m ? `${m[3]}${m[1]}${m[2]}${m[4].padStart(2, '0')}${m[5]}` : (s ?? '');
};

const HistoryTab: React.FC<Props> = ({ quoteNumber }) => {
  const [rows, setRows] = useState<QuoteHistoryRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mineToday, setMineToday] = useState(false);  // N64

  useEffect(() => {
    if (!quoteNumber) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    quoteService.getQuoteHistory(quoteNumber, mineToday)
      .then((data) => { if (!cancelled) setRows(data); })
      .catch((e: unknown) => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [quoteNumber, mineToday]);

  const columns = useMemo<ColumnDef<QuoteHistoryRow>[]>(() => [
    {
      accessorKey: 'datetime',
      header: 'Date/Time',
      size: 160,
      sortingFn: (a, b, id) => {
        const av = _dtSortKey(a.getValue(id) as string);
        const bv = _dtSortKey(b.getValue(id) as string);
        return av < bv ? -1 : av > bv ? 1 : 0;
      },
      cell: (info) => (info.getValue() as string) || '-',
    },
    {
      accessorKey: 'user',
      header: 'User',
      size: 150,
      cell: (info) => (info.getValue() as string) || '-',
    },
    {
      accessorKey: 'table',
      header: 'Section',
      size: 130,
      cell: (info) => {
        const v = info.getValue() as string;
        return TABLE_LABELS[v] ?? v;
      },
    },
    {
      accessorKey: 'column',
      header: 'Field',
      size: 170,
      cell: (info) => (info.getValue() as string) || '-',
    },
    {
      accessorKey: 'oldVal',
      header: 'Old Value',
      size: 200,
      cell: (info) => (info.getValue() as string) || '-',
    },
    {
      accessorKey: 'newVal',
      header: 'New Value',
      size: 200,
      cell: (info) => (info.getValue() as string) || '-',
    },
  ], []);

  // N64: "My changes today" lives in the TanTable header (next to search).
  const headerExtra = (
    <label className="flex items-center gap-1.5 text-[0.71875rem] text-slate-600 dark:text-slate-300 cursor-pointer select-none whitespace-nowrap">
      <input type="checkbox" checked={mineToday} onChange={(e) => setMineToday(e.target.checked)} />
      My changes today
    </label>
  );

  return (
    <TanTable
      data={rows}
      columns={columns}
      loading={loading}
      errorMessage={error}
      cardTitle="Edit History"
      searchPlaceholder="Search history…"
      settingsKey="quote-history"
      cardHeight="lg"
      enableSearch
      enableReset
      enableShowHideCols
      enableColumnFilters
      enableZebraStriping={false}
      defaultSorting={[{ id: 'datetime', desc: true }]}
      headerExtra={headerExtra}
    />
  );
};

export default HistoryTab;
